/**
 * "Relocate a resident" — the EHR-managed variant.
 *
 * Replaces relocateResident on campuses where the EHR owns resident data. The room
 * change is made in the partner system and arrives here as a task to approve on
 * /tasks/ehr.
 *
 * Same title as the portal flow; the gates are exactly complementary so only one
 * of the two is ever offered. `placement` is omitted (vestigial — see
 * SpotlightOverlay.jsx).
 *
 * See docs/INTERACTIVE_HELP_PLAN.md §5.2 for the anchors and §10 Decision 2.
 */

export default {
    id: 'relocate-resident-ehr',
    title: 'Relocate a resident',
    description: 'Approve the room change sent by your EHR.',

    minRoleWeight: 11,

    /* Exact inverse of relocateResident. */
    available: (ctx) => ctx.ehrActive,

    startsAt: 'This will take you to the EHR Tasks page.',

    steps: [
        {
            id: 'open-subtab',
            route: { path: '/tasks/ehr', navigate: true },
            anchor: 'ehr.subtab-move',
            title: 'Open the Relocate Resident tasks',
            body: 'On the Residents tab, choose Relocate Resident. The number on the tab is how many tasks are waiting. If you are looking at the Users tab, switch to Residents first.',
            /* Keyed on `.is-active` rather than [aria-selected="true"] — the
               engine observes class/style/disabled, so the class flip is caught
               instantly (detectors.js). */
            done: { type: 'dom', selector: '[data-help-id="ehr.subtab-move"].is-active' },
        },
        {
            id: 'open-task',
            route: { path: '/tasks/ehr', navigate: false },
            anchor: 'ehr.task-row',
            title: 'Open a relocate task',
            body: 'Click the row for the resident who is moving. If the list is empty there is nothing waiting for approval right now.',
            done: { type: 'dom', selector: '[data-help-id="ehr.approve-move"]' },
        },
        {
            id: 'approve',
            route: { path: '/tasks/ehr', navigate: false },
            anchor: 'ehr.approve-move',
            title: 'Check the rooms, then approve',
            body: 'The dialog names the resident and both rooms — the one they are leaving and the one they are moving to. Confirm that matches what you expect, then click Approve. Their devices and alerts follow them to the new area.',
            /* Presence, not :not([disabled]) — Approve re-disables while the write
               is in flight. */
            context: {
                selector: '[data-help-id="ehr.approve-move"]',
                contextStep: 'open-task',
            },
            done: { type: 'transaction', transTypes: ['resident_move_task'] },
            timeoutMs: 15000,
        },
    ],

    success: {
        title: 'Relocation approved',
        body: 'The move is saved. Their devices and alerts now follow the new area.',
    },
};
